import { useLocalSearchParams, useRouter } from 'expo-router'
import { useCallback, useEffect } from 'react'
import { Platform, View } from 'react-native'

import Snackbar from '@/components/snackbar/Snackbar'
import UniActivityIndicator from '@/components/unistyles/UniActivityIndicator'
import UniText from '@/components/unistyles/UniText'
import { useAppStore } from '@/store/useAppStore'
import {
  clearLoginAttemptInfo,
  consumeLinkCode,
  doesSessionExist,
} from '@/utils/auth/auth'
import log from '@/utils/datadog/log/log'

// Expo Router on web rewrites the URL, moving query params after the hash:
//   /auth/verify#linkCode?rid=passwordless&preAuthSessionId=xxx&tenantId=public
// instead of the correct format:
//   /auth/verify?preAuthSessionId=xxx&rid=passwordless&tenantId=public#linkCode
// So on web we parse the '#' param ourselves to extract both values.
function parseMagicLinkParams(routeParams: {
  preAuthSessionId?: string | string[]
  '#'?: string | string[]
}): { linkCode?: string; preAuthSessionId?: string } {
  if (Platform.OS === 'web') {
    const fragment = Array.isArray(routeParams['#'])
      ? routeParams['#'][0]
      : routeParams['#']
    if (!fragment) return {}

    const stripped = fragment.startsWith('#')
      ? fragment.substring(1)
      : fragment
    const qIndex = stripped.indexOf('?')
    if (qIndex === -1) return { linkCode: stripped }

    const linkCode = stripped.substring(0, qIndex)
    const params = new URLSearchParams(stripped.substring(qIndex + 1))
    return {
      linkCode,
      preAuthSessionId: params.get('preAuthSessionId') ?? undefined,
    }
  }

  return {
    linkCode: Array.isArray(routeParams['#'])
      ? routeParams['#'][0]
      : routeParams['#'],
    preAuthSessionId: Array.isArray(routeParams.preAuthSessionId)
      ? routeParams.preAuthSessionId[0]
      : routeParams.preAuthSessionId,
  }
}

const Verify = () => {
  const router = useRouter()
  const setAuth = useAppStore.use.setAuth()

  const routeParams = useLocalSearchParams<{
    rid: string
    preAuthSessionId: string
    tenantId: string
    '#': string
  }>()

  const handleMagicLinkClicked = useCallback(
    async (code: string, sessionId: string) => {
      try {
        const response = await consumeLinkCode(code, sessionId)

        if (response.status === 'OK') {
          await clearLoginAttemptInfo()
          if (
            response.createdNewRecipeUser &&
            response.user.loginMethods.length === 1
          ) {
            // user sign up success
          } else {
            // user sign in success
          }

          setAuth({
            accessToken: undefined,
            isLoggedIn: true,
          })
          await clearLoginAttemptInfo()
          router.navigate('/')
        } else if (response.status === 'RESTART_FLOW_ERROR') {
          log.error('auth failed with RESTART_FLOW_ERROR', response)

          Snackbar.error(
            'The magic link has expired. Please request a new one.'
          )

          await clearLoginAttemptInfo()

          router.navigate('/login')
        } else if (response.status === 'EXPIRED_USER_INPUT_CODE_ERROR') {
          log.error('auth failed with EXPIRED_USER_INPUT_CODE_ERROR', response)

          Snackbar.error('The magic link has expired. Please try again.')

          await clearLoginAttemptInfo()

          router.navigate('/login')
        } else {
          log.error(`auth failed with ${response.status}`, response)

          Snackbar.error(
            'There was a problem with your magic link. Please try again.'
          )

          await clearLoginAttemptInfo()

          router.navigate('/login')
        }
      } catch (err: any) {
        log.error('auth failed', err)
        if (err.isSuperTokensGeneralError === true) {
          log.error(
            'something happened while verifying the magic link',
            err.message
          )

          Snackbar.error(
            'There was a problem with your magic link. Please try again.'
          )
        } else {
          Snackbar.error(
            'There was a problem with your magic link. Please try again.'
          )
        }
        router.navigate('/login')
      }
    },
    [router, setAuth]
  )

  useEffect(() => {
    // A user who is currently logged in may inadvertently click on a magic link
    // If this happens we want to redirect them to the home page rather than try to use the link
    doesSessionExist().then((exists) => {
      if (exists) {
        router.navigate('/')
        return
      }
    })

    const { linkCode, preAuthSessionId } = parseMagicLinkParams(routeParams)

    if (!linkCode || !preAuthSessionId) {
      log.error('preAuthSessionId or linkCode not found in query params')
      return
    }

    handleMagicLinkClicked(linkCode, preAuthSessionId)
  }, [handleMagicLinkClicked, routeParams, router])

  return (
    <View
      style={{
        flex: 1,
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <UniActivityIndicator animating={true} size="large" />
      <UniText style={{ marginTop: 10 }}>
        Validating your login credentials...
      </UniText>
    </View>
  )
}

export default Verify
